import { Workflow } from 'src/redux/consts'
import { AsyncTask, filterAsyncTasksByWorkflow } from 'src/redux/slices/async-processing/consts'
import { asyncSuccess, startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import { AsyncSuccessPayload, StartAsyncTaskPayload } from 'src/redux/slices/async-processing/types'
import { SliceStatus } from 'src/redux/types'

import { ActionReducerMapBuilder, Draft } from '@reduxjs/toolkit'

type StartHandler<T> = {
  (state: Draft<T>, payload: { payload: StartAsyncTaskPayload }): void
}

type SuccessHandler<T> = {
  (state: Draft<T>, payload: { payload: AsyncSuccessPayload }): void
}

export type Handlers<T> = {
  onStart?: StartHandler<T>
  onSuccess?: SuccessHandler<T>
}

export type TaggedHandlers<T> = {
  [key in keyof typeof AsyncTask]?: Handlers<T>
}

type ExtraReducerConfig<T> = {
  workflow: keyof typeof Workflow
  handlers: TaggedHandlers<T>
}
interface AddAsyncExtraReducers {
  <T extends SliceStatus>(builder: ActionReducerMapBuilder<T>, config: ExtraReducerConfig<T>): void
}

export const addAsyncExtraReducers: AddAsyncExtraReducers = <T extends SliceStatus>(
  builder: ActionReducerMapBuilder<T>,
  { workflow, handlers }: ExtraReducerConfig<T>,
) => {
  builder.addCase(startAsyncTask, (state, { payload }) => {
    if (filterAsyncTasksByWorkflow(workflow).includes(payload.endpoint)) {
      state.isProcessing = true
    }
    try {
      Object.entries(handlers).forEach(([task, { onStart }]) => {
        if (task === payload.endpoint && onStart) {
          onStart(state, { payload })
        }
      })
    } catch (error) {
      console.error(error)
      state.runtimeError = { message: 'Something went wrong while initiating an async request.' }
    }
  })
  builder.addCase(asyncSuccess, (state, { payload }) => {
    if (filterAsyncTasksByWorkflow(workflow).includes(payload.endpoint)) {
      state.isProcessing = false
    }
    try {
      Object.entries(handlers).forEach(([task, { onSuccess }]) => {
        if (task === payload.endpoint && onSuccess) {
          onSuccess(state, { payload })
        }
      })
    } catch (error) {
      console.error(error)
      state.runtimeError = {
        message: 'Something went wrong while handling an async request result.',
      }
    }
  })
}
